
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Calculator, FileCheck, Clock, CreditCard } from 'lucide-react';

const PayrollPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-green-50">
      <Helmet>
        <title>PEO Payroll Processing Services | The PEO App</title>
        <meta name="description" content="Streamline your payroll with PEO services. Discover how Professional Employer Organizations simplify tax filings, ensure compliance, and provide expert payroll administration." />
        <link rel="canonical" href="https://thepeoapp.com/services/payroll" />
        <meta name="keywords" content="PEO payroll processing, PEO payroll services, Professional Employer Organization payroll, payroll administration, payroll tax compliance, multi-state payroll" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">PEO Payroll Processing Services</h1>
          
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Payroll processing through a Professional Employer Organization (PEO) streamlines one of your business's most complex and time-consuming functions. PEOs handle the entire payroll process, from calculating wages to managing tax filings, while ensuring compliance with all federal, state, and local regulations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <Calculator className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Comprehensive Payroll Management</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Accurate and timely processing of regular and special payrolls</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Direct deposit and pay card options for employees</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Wage garnishment administration</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>PTO tracking and management</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Custom reporting and payroll analytics</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md">
              <FileCheck className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Tax Management & Compliance</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Calculation, deposit, and filing of federal, state, and local payroll taxes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Quarterly and annual tax reporting</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>W-2 and 1099 preparation and distribution</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Multi-state tax compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Tax compliance with changing regulations</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <Clock className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Time & Attendance Integration</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Integration with time and attendance systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Mobile time tracking options</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Automated time approval workflows</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Seamless data transfer to payroll processing</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md">
              <CreditCard className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Employee Self-Service</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Online access to pay stubs and tax forms</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Direct deposit management</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Tax withholding adjustments</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>PTO balances and request management</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Mobile app access for on-the-go employees</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Benefits of PEO Payroll Services</h2>
            <div className="space-y-6">
              <p className="text-lg text-gray-700">
                Partnering with a PEO for payroll processing offers significant advantages for businesses of all sizes:
              </p>
              
              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li><strong>Time Savings:</strong> Reclaim hours spent on manual payroll processing and focus on growing your business</li>
                <li><strong>Reduced Errors:</strong> Minimize costly payroll mistakes that can lead to penalties and employee dissatisfaction</li>
                <li><strong>Tax Compliance:</strong> Stay current with changing tax laws across all jurisdictions where you have employees</li>
                <li><strong>Scalability:</strong> Easily manage payroll as your business grows or expands into new states</li>
                <li><strong>Expert Support:</strong> Access payroll specialists who can address complex situations and questions</li>
                <li><strong>Advanced Technology:</strong> Utilize sophisticated payroll platforms without the high cost of purchasing software</li>
              </ul>
            </div>
          </div>
          
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Simplify Your Payroll?</h2>
            <Link to="/">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Free PEO Quotes
              </Button>
            </Link>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold mb-6">PEO Payroll FAQs</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-2">How does PEO payroll processing work?</h3>
                <p className="text-gray-700">
                  With PEO payroll, your business enters employee hours and payroll information into the PEO's system. The PEO then calculates wages, deductions, and taxes, processes payments, and handles all tax filings and deposits under their federal employer identification number (FEIN) as part of the co-employment relationship.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2">Can a PEO handle payroll for employees in multiple states?</h3>
                <p className="text-gray-700">
                  Yes, one of the major advantages of PEO payroll services is their ability to manage multi-state payroll processing. PEOs maintain compliance with varying state tax requirements, unemployment insurance rates, and other state-specific payroll regulations.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2">Will my employees notice a difference with PEO payroll?</h3>
                <p className="text-gray-700">
                  The transition to PEO payroll is usually seamless for employees. While their pay stubs may show the PEO's name alongside your company's, direct deposits continue uninterrupted. Employees often gain the benefit of improved self-service access to their payroll information and more detailed pay stubs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PayrollPage;
